import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type SelectHTMLAttributes,
} from 'react';

import { parseSelectOptions } from './parseSelectOptions';
import { useMenuPortal, type MenuPlacementPreference } from './useMenuPortal';

type AcademiaSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  fieldSize?: 'sm' | 'md';
  menuPlacement?: MenuPlacementPreference;
};

function joinClasses(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(' ');
}

export default function AcademiaSelect({
  className,
  fieldSize = 'md',
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  name,
  id: idProp,
  required,
  'aria-label': ariaLabel,
  menuPlacement = 'auto',
}: AcademiaSelectProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const options = useMemo(() => parseSelectOptions(children), [children]);

  const controlledValue = value !== undefined ? String(value) : undefined;
  const [internalValue, setInternalValue] = useState(
    () => String(defaultValue ?? options[0]?.value ?? ''),
  );
  const currentValue = controlledValue ?? internalValue;

  useEffect(() => {
    if (controlledValue !== undefined) setInternalValue(controlledValue);
  }, [controlledValue]);

  const { menuRef, style } = useMenuPortal(open, () => setOpen(false), anchorRef, menuPlacement);

  const selected = options.find((o) => o.value === currentValue);
  const displayLabel =
    selected?.label ||
    options.find((o) => o.value === '')?.label ||
    'Sélectionner…';

  const emitChange = (next: string) => {
    if (controlledValue === undefined) setInternalValue(next);
    onChange?.({
      target: { value: next, name },
      currentTarget: { value: next, name },
    } as ChangeEvent<HTMLSelectElement>);
  };

  const sizeClass = fieldSize === 'sm' ? 'academia-select-trigger--sm' : 'academia-select-trigger--md';

  const menu =
    open && options.length > 0
      ? createPortal(
          <div
            ref={menuRef}
            style={style}
            className={joinClasses(
              'academia-select-menu max-h-60 overflow-auto',
              fieldSize === 'sm' && 'academia-select-menu--sm',
            )}
          >
            <ul role="listbox" aria-labelledby={id}>
            {options.map((opt) => {
              const isSelected = opt.value === currentValue;
              return (
                <li key={`${opt.value}-${opt.label}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={opt.disabled}
                    className={joinClasses(
                      'academia-select-option w-full text-left',
                      isSelected && 'academia-select-option--selected text-white',
                      opt.disabled && 'cursor-not-allowed opacity-40',
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (opt.disabled) return;
                      emitChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={anchorRef} className={joinClasses('relative w-full min-w-0', className)}>
      {name ? <input type="hidden" name={name} value={currentValue} /> : null}
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-required={required}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={joinClasses(
          'academia-select-trigger',
          sizeClass,
          open && 'academia-select-trigger--open',
        )}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        <span className="academia-select-trigger__label min-w-0 flex-1 truncate text-left">
          {displayLabel}
        </span>
        <ChevronDown
          className={joinClasses(
            'h-4 w-4 shrink-0 text-primary transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
}
